import {createAsyncThunk} from '@reduxjs/toolkit'

import {merge} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'

import {
    closeExistingPopup,
    unsubscribeMessage,
    subscribeMessage,
    openPopup as openPopupUtil,
} from '@web/helpers/browser'
import {isUrlValid} from '@web/helpers/path'

type ResultMessage = string

type Bridge = {
    /**
     * Bridge 는 외부 페이지에게 결과를 반환할 URL 을 전달합니다.
     * 이때 URL 은 pages/PopupBridge 를 사용하며,
     * 외부에서 접근 할 수 있는 queryKey 와
     * Bridge 를 구분할 수 있는 key 와 원하는 결과인 message 조합한 URL 을 전달합니다.
     * Bridge Page 에서는 전달받은 내용을 바탕으로 postMessage 를 통해 결과를 전달하게 되고,
     * 원하는 결과를 onCallback 으로 사용처에 전달합니다.
     * ex) ~~?returnUrl=popupBridgePage/registration_bank/success
     *     ~~?{queryKey}=popupBridgePage/{bridgeKey}/{resultMessage}
     */
    bridge: {
        url: string
        /**
         * bridge page 를 구분하기위한 key
         * - key 값으로 원하는 bridge page 를 구분하는 값이 되며,
         * postMessage 를 통해 함께 넘어오게 됩니다
         **/
        key: string
        /**
         * 결과를 전달받을 Url 를 구성하는 query
         * - query-string 으로 전달할 key 과
         * 어떤 결과를 전달 받았는지를 확인하기 위한 ResultMessage 로 구성되고,
         * 최종적으로 URL 형식으로 생성됩니다.
         * ex) { returnUrl: 'success' }
         **/
        query: {
            [QueryStringKey: string]: ResultMessage
        }
    }
}

function getUrlByQuery(url: string, query?: Bridge) {
    const {url: copyUrl, query: copyQuery} = qs.parseUrl(url)
    let result = copyQuery ?? {}
    if (query) {
        if ('bridge' in query) {
            // query string 에 전달받을 page 경로를 전달하여, 결과페이지에 따른 값을 postMessage 로 전달 받는다.
            result = merge(
                result,
                Object.keys(query.bridge.query).reduce(
                    (acc, queryKey) =>
                        merge(acc, {
                            [queryKey]: `${query.bridge.url}/${query.bridge.key}/${query.bridge.query[queryKey]}`,
                        }),
                    {},
                ),
            )
        }
    }
    return qs.stringifyUrl({
        url: copyUrl,
        query: result,
    })
}

function getComposedUrl(url: string, query?: Bridge) {
    // 상대경로로 제출된 주소는 절대경로로 바꾸어 제출한다
    const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url

    return query ? getUrlByQuery(fullUrl, query) : fullUrl
}

type Size = {
    left?: number
    top?: number
    width: number
    height: number
}

type DefaultPopupPostMessageData = {key: string; message: string; query: Record<string, string>}

const PopupLeaveMethod = {
    KEEP: 'keep',
    CLOSE: 'close',
} as const

type NewPopupWindowParam = {
    isNewTab?: never
}
type NewTabParam = {
    isNewTab?: true
}
type PopupLeaveType = Constant<typeof PopupLeaveMethod>
type WindowOpenPrams<ResolvingDataType> = {
    key: string
    url: string
    title?: string
    shouldCenter?: boolean
    size?: Size
    /**
     * 외부 Popup 에서 처리후 결과를
     * - callback : query-string 으로 전달한 함수명 결과값을 전달받는다
     * - bridge : Popup Bridge Url 로부터 결과값을 전달받는다 */
    query?: Bridge
    handleCallback?: (data: ResolvingDataType) => void
    handleClose?: (code: string) => void
    /**
     * 팝업을 띄운 페이지가 화면을 떠날때, 팝업의 처리방식 제어 필요
     * - 팝업이 유지되어야 하는 경우가 존재 (구매확정)
     */
    leaveType?: PopupLeaveType
    /** 열려있는 popup과 다른 url로 popup open 시도할 경우 닫고 새로 오픈할지 여부 */
    refreshPopupOnUrlDiff?: boolean
}
type OpenPopupParams<ResolvingDataType> =
    | (WindowOpenPrams<ResolvingDataType> & NewPopupWindowParam)
    | (WindowOpenPrams<ResolvingDataType> & NewTabParam)
export const openPopup = <ResolvingDataType = DefaultPopupPostMessageData>(
    openPopupParams: OpenPopupParams<ResolvingDataType>,
) => {
    const thunk = createAsyncThunk<
        ResolvingDataType | void,
        OpenPopupParams<ResolvingDataType>,
        {
            rejectValue: {
                code: string
                message: string
            }
        }
    >('application/openPopup', async (args, {rejectWithValue}) => {
        const {
            key,
            size,
            query,
            url,
            title,
            shouldCenter,
            handleCallback,
            handleClose,
            leaveType = PopupLeaveMethod.CLOSE,
            refreshPopupOnUrlDiff = false,
        } = args
        // 빈 스트링이거나, domain 정보가 없거나 /로 시작하지도 않는 url 전달 된 경우
        const urlInvalid = !(isUrlValid(url) || url.startsWith('/'))

        if (urlInvalid) {
            return rejectWithValue({
                code: 'URL_ERROR',
                message: 'URL 을 확인해주세요',
            })
        }

        // 상대경로로 제출된 주소는 절대경로로 바꾸어 제출한다
        const composedUrl = getComposedUrl(url, query)

        // 동일 키 사용하지만 다른 url로 열려있는 창이 있다면 닫고 새로운 정보로 오픈시도한다.
        if (refreshPopupOnUrlDiff) {
            await closeExistingPopup({key, url: composedUrl})
        }

        return new Promise<ResolvingDataType | void>((resolve, reject) => {
            const waitsClosing = !!handleClose
            let messageResolved = false
            const postMessageCallback = (postedData: ResolvingDataType) => {
                messageResolved = true
                handleCallback?.(postedData)
                !waitsClosing && resolve(postedData)
            }

            subscribeMessage({messageKey: key, subscriber: postMessageCallback})

            openPopupUtil({
                key,
                url: composedUrl,
                size, // isNewTab과 size 파라메터는 배타적 관계이므로 isNewTab 이 전달되면 size는 undefiend
                shouldCenter,
                title,
                leaveType,
            })
                .then(() => {
                    // 팝업 블록이나 외부 팝업 매니저에서 객체가 사라진것이 아니라 정상 엑션으로 팝업 종결된 경우
                    handleClose?.(messageResolved ? 'RESOLVED' : 'CLOSE')
                    resolve()
                })
                .catch((reason) => {
                    handleClose?.(reason)
                    reject(reason)
                })
                .finally(() => {
                    unsubscribeMessage({messageKey: key, subscriber: postMessageCallback})
                })
        })
    })

    return thunk(openPopupParams)
}

export const closePopup = createAsyncThunk<
    string | void,
    {key: string; url: string; query?: Bridge},
    {
        rejectValue: {
            code: string
            message: string
        }
    }
>('application/closePopup', async (args) => {
    const {key, query, url} = args

    const composedUrl = getComposedUrl(url, query)

    return new Promise<string | void>((resolve) => {
        closeExistingPopup({key, url: composedUrl}).then((closed) => {
            // 동일 url 팝업밖에 없어서 팝업이 닫히지 않으면 CLOSE code 없이 resolve
            closed ? resolve('POPUP_UNREGISTERED') : resolve()
        })
    })
})

export const postPopupMessage = createAsyncThunk<
    void,
    {
        /** 메시지에 포함할 데이터 */
        data: {
            /**
             * 메시지의 유형
             * @example 'TASK_FINISHED'
             */
            type: string
            /** 메시지에 포함할 세부 데이터 */
            content?: object
            /** 메시지 발신자 */
            from: string
        }
        /**
         * 메시지를 전달할 origin
         * @default window.location.origin
         */
        targetOrigin?: string
        shouldClose?: boolean

        /** @deprecated 하위호환용) 전달이 필요한 데이터는 data.content에 포함 */
        key?: string
    },
    {
        rejectValue: {
            code: string
            message: string
        }
    }
>(
    'application/postPopupMessage',
    ({data, targetOrigin = window.location.origin, shouldClose, key}, {rejectWithValue}) => {
        if (!window.opener) {
            return rejectWithValue({
                code: 'OPENER_ERROR',
                message: 'EMPTY_OPENER',
            })
        }
        window.opener?.postMessage(
            {
                type: data.type,
                content: data.content,
                from: data.from,

                // 하위호환용
                key,
            },
            targetOrigin,
        )
        if (shouldClose) {
            window.self.close()
        }
        return undefined
    },
)
